import { PaperData } from '../types/paper';

export const seedCrisprGeneEditing: PaperData = {
  id: 'topic-crispr-gene-editing',
  title: 'CRISPR-Cas9 Gene Editing',
  authors: ['Jennifer Doudna', 'Emmanuelle Charpentier', 'Feng Zhang'],
  year: '2012',
  arxivId: 'Genome Editing',
  abstract: 'CRISPR-Cas9 is a revolutionary RNA-guided endonuclease system adapted from bacterial adaptive immunity for targeted genomic editing in eukaryotic cells via site-specific double-strand breaks (DSBs) and repair mechanisms.',
  
  lineageGraph: {
    nodes: [
      {
        id: 'restriction_enzymes_1970',
        label: 'Restriction Endonucleases',
        type: 'ancestor',
        year: '1970',
        authors: 'Hamilton Smith, Daniel Nathans',
        description: 'Enzymes cleaving DNA at specific palindrome recognition sequences.',
        keyInnovation: 'Sequence-specific enzymatic DNA cleavage.'
      },
      {
        id: 'zfn_1996',
        label: 'Zinc Finger Nucleases (ZFNs)',
        type: 'ancestor',
        year: '1996',
        authors: 'Chandrasegaran et al.',
        description: 'Engineered proteins fusing zinc finger DNA-binding domains with FokI nuclease.',
        keyInnovation: 'Modular protein-directed DNA cleavage.'
      },
      {
        id: 'crispr_2012',
        label: 'CRISPR-Cas9 RNA-Guided System',
        type: 'target_paper',
        year: '2012',
        authors: 'Jinek, Chylinski, Charpentier, Doudna',
        description: 'Programmable dual-RNA or single-guide RNA (sgRNA) directing Cas9 endonuclease to introduce DSBs near PAM motifs.',
        keyInnovation: 'RNA-guided programmable genome targeting.'
      },
      {
        id: 'base_editing_2016',
        label: 'Base Editing (Cytosine/Adenine)',
        type: 'descendant',
        year: '2016',
        authors: 'Alexis Komor & David Liu',
        description: 'Catalytically impaired Cas9 nickase fused to deaminase enzymes converting single nucleotides without DSBs.',
        keyInnovation: 'DSB-free single nucleotide conversion ($C \\to T$ or $A \\to G$).'
      },
      {
        id: 'prime_editing_2019',
        label: 'Prime Editing (pegRNA)',
        type: 'descendant',
        year: '2019',
        authors: 'Andrew Anzalone & David Liu',
        description: 'Cas9 nickase fused to engineered reverse transcriptase guided by prime editing guide RNA (pegRNA).',
        keyInnovation: 'Search-and-replace editing without DSBs or donor DNA.'
      }
    ],
    links: [
      { source: 'restriction_enzymes_1970', target: 'zfn_1996', label: 'Evolved to modular domains' },
      { source: 'zfn_1996', target: 'crispr_2012', label: 'Replaced complex protein engineering with RNA' },
      { source: 'crispr_2012', target: 'base_editing_2016', label: 'Fused nickase with deaminase' },
      { source: 'crispr_2012', target: 'prime_editing_2019', label: 'Added reverse transcriptase guide' }
    ]
  },

  concepts: [
    {
      id: 'sgrna_pam',
      title: 'sgRNA Guidance & PAM Recognition',
      category: 'core_innovation',
      definition: 'Cas9 binding requires a 20-nucleotide guide RNA sequence complementary to the target DNA and a Protospacer Adjacent Motif (PAM, $5\'\\text{-NGG-}3\'$) directly downstream of the target site.',
      mathFormulas: [
        {
          label: 'Target Binding Constraint',
          latex: '\\text{Target DNA} = 5\'\\text{-(N)}_{20}\\text{-NGG-}3\'',
          explanation: 'Cas9 scans DNA for the NGG PAM motif before unwinding the duplex to match the 20-nt guide RNA.'
        }
      ],
      intuition: 'The sgRNA acts as a GPS coordinate, while the PAM site serves as the security password. Cas9 will not unwind or cleave DNA unless the NGG password is found adjacent to the 20-letter RNA target sequence.',
      keyNuances: [
        'PAM sequence ($5\'\\text{-NGG-}3\'$ for SpCas9) is absent from the bacterial CRISPR locus to prevent self-cleavage.',
        'Mismatches in the 8-12 bp "seed region" adjacent to PAM abolish Cas9 cleavage.'
      ],
      codeSnippet: {
        language: 'python',
        code: `def find_crispr_pam_sites(dna_sequence: str):
    import re
    # Match 20nt protospacer followed by NGG PAM
    pattern = r'(?=([ATCG]{20}[ATCG]GG))'
    matches = [m.start() for m in re.finditer(pattern, dna_sequence)]
    return [dna_sequence[i:i+23] for i in matches]

dna = "ATGCGATCGATCGATCGATCGATCGGGATCG"
targets = find_crispr_pam_sites(dna)`,
        explanation: 'Python regex script identifying 20-nt CRISPR guide targets ending with SpCas9 NGG PAM motifs.'
      },
      ancestors: [
        {
          paperTitle: 'Science',
          authorsYear: 'Jinek et al., 2012',
          concept: 'sgRNA Chimera',
          relationship: 'Fused crRNA and tracrRNA into a single guide RNA molecule.'
        }
      ],
      descendants: [
        {
          concept: 'Prime Editing',
          paperOrField: 'Liu et al., 2019',
          impact: 'Extended sgRNA into pegRNA carrying reverse transcriptase template.'
        }
      ],
      citations: ['Jinek et al., 2012']
    },

    {
      id: 'dsb_repair',
      title: 'DSB Repair: NHEJ vs HDR',
      category: 'architectural_component',
      definition: 'Cas9 introduces a blunt double-strand break (DSB) 3 base pairs upstream of the PAM. The cell repairs the DSB via error-prone Non-Homologous End Joining (NHEJ) or template-directed Homology-Directed Repair (HDR).',
      mathFormulas: [
        {
          label: 'Repair Outcomes',
          latex: '\\text{DSB} \\xrightarrow{\\text{NHEJ}} \\text{Indel Knockout}, \\quad \\text{DSB} + \\text{Donor} \\xrightarrow{\\text{HDR}} \\text{Precise Knock-In}',
          explanation: 'NHEJ creates frameshift indels for gene knockouts; HDR uses donor template for precise sequence insertion.'
        }
      ],
      intuition: 'NHEJ is like emergency duct tape—quick but messy, producing random insertions/deletions (indels) that break gene function. HDR is like a precise blueprints rewrite—replacing the broken region with an exact donor DNA template.',
      keyNuances: [
        'NHEJ operates throughout the cell cycle, whereas HDR occurs predominantly during S/G2 phases.',
        'Efficiency of HDR is typically much lower ($1\\text{--}10\\%$) compared to NHEJ ($>50\\%$).'
      ],
      codeSnippet: {
        language: 'python',
        code: `def simulate_nhej_frameshift(coding_dna: str, cut_pos: int, indel_len: int):
    # Simulate frameshift insertion or deletion
    edited_dna = coding_dna[:cut_pos] + coding_dna[cut_pos + indel_len:]
    is_frameshift = (len(coding_dna) - len(edited_dna)) % 3 != 0
    return edited_dna, is_frameshift`,
        explanation: 'Simulates NHEJ repair indel resulting in gene loss-of-function frameshift.'
      },
      ancestors: [
        {
          paperTitle: 'Cell',
          authorsYear: 'Smith et al., 1970',
          concept: 'Endonuclease DSB',
          relationship: 'Established DNA double-strand cleavage mechanics.'
        }
      ],
      descendants: [
        {
          concept: 'Base Editing',
          paperOrField: 'Komor et al., 2016',
          impact: 'Eliminated reliance on DSB and HDR pathways.'
        }
      ],
      citations: ['Doudna & Charpentier, 2014']
    }
  ],

  flashcards: [
    {
      id: 'fc-c1',
      conceptId: 'sgrna_pam',
      question: 'What is the PAM sequence required by SpCas9, and where is it located relative to the target DNA?',
      answer: 'SpCas9 requires a **$5\'\\text{-NGG-}3\'$** Protospacer Adjacent Motif (PAM) located directly **$3\'$ (downstream)** of the 20-nucleotide protospacer target sequence.',
      formula: '5\'\\text{-(N)}_{20}\\text{-NGG-}3\'',
      difficulty: 'medium',
      tags: ['CRISPR', 'PAM', 'Cas9', 'Genomics']
    },
    {
      id: 'fc-c2',
      conceptId: 'dsb_repair',
      question: 'How do the cell repair outcomes differ between NHEJ and HDR following a Cas9 double-strand break?',
      answer: '• **NHEJ (Non-Homologous End Joining)**: Fast, error-prone repair causing insertion/deletion (indel) frameshifts that **knock out** gene function.\n• **HDR (Homology-Directed Repair)**: Precise repair utilizing an exogenous donor DNA template for targeted **knock-in** sequence editing.',
      difficulty: 'medium',
      tags: ['CRISPR', 'NHEJ', 'HDR', 'Gene Editing']
    },
    {
      id: 'fc-c3',
      conceptId: 'sgrna_pam',
      question: 'Why does the bacterial host genome not suffer self-cleavage at its own CRISPR locus?',
      answer: 'Because the bacterial host CRISPR repeat-spacer array **lacks the PAM ($5\'\\text{-NGG-}3\'$) motif**, preventing Cas9 from unwinding or cleaving host DNA.',
      difficulty: 'hard',
      tags: ['CRISPR', 'Immunity', 'PAM', 'Biology']
    },
    {
      id: 'fc-c4',
      conceptId: 'dsb_repair',
      question: 'What is the exact location of the blunt double-strand break introduced by SpCas9 relative to the PAM site?',
      answer: 'SpCas9 cleaves target DNA **3 base pairs upstream ($5\'$)** of the PAM sequence using its HNH and RuvC nuclease domains.',
      difficulty: 'hard',
      tags: ['Cas9', 'Cleavage', 'Nuclease', 'CRISPR']
    }
  ]
};
